import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogActions } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useDebounce } from '@dhl/utils'
import { DialogTitleClosable, Pagination, SearchTextField, type GridColDef, type GridRowModel } from '@dhl/components'
import { Button, Checkbox, Grid, H5 } from '@dhl-ui/components'
import { AdminDataGrid } from '~/common'

export type UpdateDataFn = (ids: string[]) => void
export type GetDataFn<T> = (page: number, perPage: number, search: string) => Promise<[number, T[]]>
type Props<T extends GridRowModel = any> = {
  open: boolean
  title: string
  columns: GridColDef<T>[]
  getData: GetDataFn<T>
  onUpdate: UpdateDataFn
  onClose?: () => void
  itemsPerPage?: number
  disabledRowIds?: string[]
  cancelBtnTitle?: string
  saveBtnTitle?: string
}

export const SelectableDataTableDialog = <T extends GridRowModel = any>({
  open,
  title,
  columns,
  getData,
  onUpdate,
  onClose = () => {},
  disabledRowIds = [],
  saveBtnTitle,
  cancelBtnTitle,
}: Props<T>) => {
  const { t } = useTranslation()
  const [page, setPage] = useState<number>(0)
  const [perPage, setPerPage] = useState<number>(10)
  const [total, setTotal] = useState<number>(0)
  const [search, setSearch] = useState<string>('')
  const [rows, setRows] = useState<T[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const debouncedSearch = useDebounce(search, 500)

  const handleSave: UpdateDataFn = (ids) => {
    onUpdate(ids)
    onClose()
  }

  const enrichedColumns: GridColDef<T>[] = [
    {
      field: '_select',
      headerName: ' ',
      width: 60,
      sortable: true,
      renderCell: ({ row: { id } }) => (
        <Checkbox
          checked={selectedIds.includes(id)}
          onChange={() => {
            setSelectedIds(selectedIds.includes(id) ? selectedIds.filter((i) => i !== id) : [...selectedIds, id])
          }}
        />
      ),
    },
    ...columns,
  ]
  useEffect(() => {
    getData(page, perPage, debouncedSearch).then(([total, payload]) => {
      setTotal(total)
      setRows(payload)
    })
  }, [page, debouncedSearch])

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitleClosable onClose={onClose}>
          <H5>{title}</H5>
        </DialogTitleClosable>
        <DialogContent>
          <Grid container direction="column" gap={1}>
            <Grid>
              <SearchTextField onChange={setSearch} searchTerm={search} fullWidth />
            </Grid>
            <Grid>
              <AdminDataGrid
                columns={enrichedColumns}
                rows={rows}
                getRowClassName={({ row: { id } }) => (disabledRowIds.includes(id) ? '--selectable-disabled' : '')}
                hideFooter
                sx={{
                  '& .--selectable-disabled': {
                    pointerEvents: 'none',
                    cursor: 'not-allowed',
                    opacity: 0.5,
                  },
                }}
              />
            </Grid>
            <Grid>
              <Pagination
                page={page}
                count={total}
                rowsPerPage={perPage}
                onPageChange={(_, newPage) => {
                  setPage(newPage)
                }}
                onRowsPerPageChange={setPerPage}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            {cancelBtnTitle ?? t('admin.form.cancel')}
          </Button>
          <Button variant="contained" onClick={() => handleSave(selectedIds)}>
            {saveBtnTitle ?? t('admin.form.saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
